import {
  AttachFile,
  CenterFocusWeak,
  Mood,
  MoreVert,
  Search,
  Send,
  StarBorder
} from '@mui/icons-material'
import {
  Avatar,
  Badge,
  Box,
  BoxProps,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Popper,
  TextField,
  Typography,
  styled
} from '@mui/material'
import React from 'react'
import { connect } from 'react-redux'
import ChatBubble from 'components/messages/chat-bubble'
import EmojiPicker from 'emoji-picker-react'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as MessageOperations,
  operations as messageOperations
} from 'states/dux/message/operations'
import { Payload as MessagePayload } from 'states/dux/message/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import {
  Operations as UserOperations,
  operations as userOperations
} from 'states/dux/user/operations'
import { Payload as UserPayload } from 'states/dux/user/types'
import State from 'states/types'
import { NivosMessage } from 'types/nivos-message'
import { NivosPublicUser, NivosUserGroup } from 'types/user'
import { mapOperationsToDispatchProps, post, trusty } from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  user: UserPayload
  message: MessagePayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    user: UserOperations
    message: MessageOperations
  }
}

interface OwnProps {
  kausap: NivosPublicUser | NivosUserGroup
  messages: NivosMessage[]
  isaGroupChat?: boolean
  containerProps?: BoxProps
  fillSpace?: boolean
  hideSendButton?: boolean
}

type AppProps = StateProps & DispatchProps & OwnProps

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#44b700',
    color: '#44b700',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""'
    }
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0
    }
  }
}))

const ChatBoxFC: React.FC<any> = (props: AppProps) => {
  const {
    theme,
    user,
    messages,
    kausap,
    isaGroupChat,
    containerProps,
    fillSpace,
    hideSendButton,
    operations
  } = props
  const bottomOfConversation = React.useRef<HTMLElement>(null)
  const emojiPickerBtn = React.useRef<HTMLButtonElement>(null)
  const [messageList, setMessages] = React.useState<NivosMessage[]>([])
  const [messageText, setMessage] = React.useState('')
  const [emojiPickerOpen, setEmojiPickerOpen] = React.useState(false)
  const [sending, setSending] = React.useState(false)

  const _isSent = (_message: NivosMessage): boolean => {
    return _message.createdBy.id === user.id
  }
  const _getContactImageURL = (): string => {
    if ('image' in kausap) {
      return kausap.image !== undefined ? kausap.image.url : ''
    }
    return ''
  }

  const _getContactTitle = (): string => {
    if ('fullName' in kausap) {
      return kausap.fullName
    } else if ('name' in kausap) {
      return kausap.name
    }
    return ''
  }
  const sendMessage = (): void => {
    setSending(true)
    void post('/message/', {
      message: messageText,
      receiver: trusty(isaGroupChat) ? null : kausap.id,
      group_message: trusty(isaGroupChat) ? kausap.id : null,
      created_by: user.id
    })
      .then(data => {
        if (data.id !== undefined) operations.message.add(data)
        setMessage('')
        setSending(false)
      })
      .catch(err => {
        console.log(err)
        setSending(false)
      })
  }

  React.useEffect(() => {
    if (bottomOfConversation.current != null) {
      bottomOfConversation.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [])

  React.useEffect(() => setMessages(messages), [messages])

  return (
    <Paper
      style={{
        width: trusty(fillSpace) ? '100%' : 'fit-content',
        height: trusty(fillSpace) ? '100%' : 'fit-content'
      }}
      elevation={8}
    >
      <Box
        width={trusty(fillSpace) ? '100%' : 400}
        minHeight={trusty(fillSpace) ? '100%' : 600}
        height={trusty(fillSpace) ? '100%' : 'fit-content'}
        display={'flex'}
        justifyContent={trusty(fillSpace) ? 'flex-start' : 'center'}
        flexDirection={'column'}
        {...containerProps}
      >
        <Popper
          open={emojiPickerOpen}
          anchorEl={emojiPickerBtn.current}
          placement={'top'}
          sx={{
            marginTop: -10,
            marginLeft: -8,
            zIndex: 3
          }}
        >
          <EmojiPicker
            lazyLoadEmojis
            onEmojiClick={emoji => setMessage(messageText + emoji.emoji)}
          />
        </Popper>
        <Box height={85} padding={4} display={'flex'} alignItems={'center'}>
          <StyledBadge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            variant="dot"
          >
            <Avatar
              sx={{ width: '75px', height: '75px' }}
              src={_getContactImageURL()}
            />
          </StyledBadge>
          <Box marginLeft={2} flexGrow={1} overflow={'hidden'} maxHeight={1}>
            <Typography variant={'h6'}>
              <strong>{_getContactTitle()}</strong>
            </Typography>
            <Typography>Online - Last seen, 2:02pm</Typography>
          </Box>
          {trusty(fillSpace) && (
            <>
              <IconButton sx={{ marginRight: 2, fontSize: '30px' }}>
                <StarBorder fontSize={'inherit'} />
              </IconButton>
              <IconButton sx={{ marginRight: 2, fontSize: '30px' }}>
                <Search fontSize={'inherit'} />
              </IconButton>
              <IconButton sx={{ marginRight: 2, fontSize: '30px' }}>
                <MoreVert fontSize={'inherit'} />
              </IconButton>
            </>
          )}
        </Box>
        <Divider variant={'middle'} color={'#d4d2d2'} />
        <Box paddingY={2} />
        <Box
          width={1}
          flexGrow={1}
          maxHeight={
            trusty(fillSpace) ? 'calc(100vh - 60px - 85px - 90px)' : 600
          }
          overflow={'auto'}
        >
          {messageList.map((message, index) => (
            <ChatBubble
              key={index}
              type={_isSent(message) ? 'sent' : 'received'}
              message={message}
            />
          ))}
          {messageList.length === 0 && (
            <Box
              display={'flex'}
              justifyContent={'center'}
              alignItems={'center'}
              width={1}
              height={1}
            >
              <Typography variant={'h3'}>No messages..</Typography>
            </Box>
          )}
          <Box ref={bottomOfConversation} />
        </Box>
        <Box
          padding={4}
          height={'fit-content'}
          display={'flex'}
          alignItems={'center'}
        >
          <TextField
            multiline
            fullWidth
            value={messageText}
            onChange={event => setMessage(event.currentTarget.value)}
            onKeyDown={event => {
              if (event.key === 'Enter' && trusty(hideSendButton)) sendMessage()
            }}
            placeholder={'Enter new message..'}
            InputProps={{
              startAdornment: (
                <IconButton sx={{ alignSelf: 'flex-start' }}>
                  <AttachFile />
                </IconButton>
              ),
              endAdornment: (
                <InputAdornment
                  position={'end'}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  <IconButton
                    ref={emojiPickerBtn}
                    onClick={() => setEmojiPickerOpen(!emojiPickerOpen)}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    <Mood />
                  </IconButton>
                  <IconButton sx={{ alignSelf: 'flex-start' }}>
                    <CenterFocusWeak />
                  </IconButton>
                </InputAdornment>
              ),
              sx: {
                padding: 1,
                borderRadius: 5,
                backgroundColor: theme.quaternaryColor
              }
            }}
          />
          {!trusty(hideSendButton) && (
            <Box height={'fit-content'} marginLeft={4}>
              <Button
                variant={'outlined'}
                sx={{ borderRadius: 5, padding: 2.5 }}
                size={'large'}
                startIcon={<Send />}
                onClick={() => sendMessage()}
                disabled={sending}
              >
                {!sending && 'Send'}
                {sending && <CircularProgress sx={{ height: 24, width: 24 }} />}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    user: state.user,
    message: state.message
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  user: userOperations,
  message: messageOperations
})

const ChatBox = connect(
  mapStateToProps,
  mapDispatchToProps
)(ChatBoxFC) as React.FC<OwnProps>

export default ChatBox
